import { MenuItem } from "@mui/material"
import type { FC, RefObject } from "react"
import { useMemo, useRef, useState } from "react"
import { useQuery } from "urql"

import MultiSelect from "@/components/MultiSelect/MultiSelect"
import { MENU_PROPS } from "@/constants/forms"
import type { CompanyListFilter, Query } from "@/graphql/codegen/graphql"
import { CompanyAccountingType, CompanyBaseListDocument, CompanyStatus } from "@/graphql/codegen/graphql"
import { useGetBaseCompanies } from "@/hooks/useGetBaseCompanies"
import { useScrollLoadMore } from "@/hooks/useScrollLoadMore"

interface DistributorMultiSelectProps {
  value: string[]
  onChange: (distributorIds: string[]) => void
  error?: boolean
  disabled?: boolean
  placeholder?: string
  id?: string
}

const DISTRIBUTOR_FILTER: CompanyListFilter = {
  types: [CompanyAccountingType.Customer, CompanyAccountingType.Vendor],
  statuses: [CompanyStatus.Active],
}

export const DistributorMultiSelect: FC<DistributorMultiSelectProps> = ({
  value,
  onChange,
  error,
  disabled,
  placeholder = "Select Distributors",
  id = "distributor-select",
}) => {
  const distributorsSelectRef = useRef<HTMLDivElement>(null)
  const [initialValue] = useState(value)

  const { companies, isFetchingCompanies, loadMoreCompanies } = useGetBaseCompanies(DISTRIBUTOR_FILTER)

  // Fetch initially selected companies by IDs to ensure they're loaded even if beyond first page
  const [{ data: selectedData }] = useQuery<Pick<Query, "company">>({
    query: CompanyBaseListDocument,
    variables: {
      input: {
        filter: {
          ...DISTRIBUTOR_FILTER,
          companyIds: initialValue,
        },
        limit: initialValue.length,
        offset: 0,
      },
    },
    pause: initialValue.length === 0,
  })

  const distributorOptions = useMemo(() => {
    const selectedCompanies =
      selectedData?.company.list.__typename === "CompanyListSuccess" ? selectedData.company.list.companies : []

    const selectedIds = new Set(selectedCompanies.map((c) => c.companyId))

    return [...selectedCompanies, ...companies.filter((c) => !selectedIds.has(c.companyId))].map((company) => ({
      value: company.companyId,
      label: company.name ?? "",
    }))
  }, [companies, selectedData])

  const getSelectMenuProps = (ref: RefObject<unknown>) => ({
    ...MENU_PROPS,
    PaperProps: {
      ...MENU_PROPS.PaperProps,
      ref,
    },
  })

  const handleDistributorsScroll = useScrollLoadMore({
    fetching: isFetchingCompanies,
    loadMore: loadMoreCompanies,
    scrollContainerRef: distributorsSelectRef,
  })

  return (
    <MultiSelect
      id={id}
      value={value}
      onChange={onChange}
      options={distributorOptions}
      placeholder={placeholder}
      width='full'
      disabled={disabled || isFetchingCompanies}
      error={error}
      onScrollCapture={handleDistributorsScroll}
      MenuProps={getSelectMenuProps(distributorsSelectRef)}
    >
      {distributorOptions.map((option) => (
        <MenuItem key={option.value} value={option.value}>
          {option.label}
        </MenuItem>
      ))}
    </MultiSelect>
  )
}
